import {
    useEffect,
    useRef,
    useState,
} from "react";

import {
    useNavigate,
} from "react-router-dom";

import {
    useDispatch,
} from "react-redux";

import {
    setJourney,
} from "../../../store/journeySlice";

import type {
    AppDispatch,
} from "../../../store";

import Lottie from "lottie-react";

import kycLoaderAnimation
    from "../../../assets/Review ID.json";

import MobileFrame
    from "../../../components/MobileFrame";

import {
    ChevronLeft,
    Loader2
} from "lucide-react";

import {
    completeDigiLocker,
    journeyAction,
} from "../../../api/journey.api";

import { ApiError } from "../../../api/ApiError";


const DigiLockerCallback = () => {

    const navigate =
        useNavigate();

    const dispatch =
        useDispatch<AppDispatch>();

    const [error, setError] =
        useState<string | null>(null);

    const [isRetrying, setIsRetrying] =
        useState(false);

    const hasHandledCallback =
        useRef(false);


    useEffect(() => {

        if (hasHandledCallback.current) {
            return;
        }

        hasHandledCallback.current = true;


        const handleDigiLockerCallback =
            async () => {

                try {

                    // console.log(
                    //     "=== DIGILOCKER CALLBACK ==="
                    // );


                    const params =
                        new URLSearchParams(
                            window.location.search
                        );


                    const eaadhaar =
                        params.get("eaadhaar");

                    const urn =
                        params.get("urn");


                    // console.log(
                    //     "eaadhaar:",
                    //     eaadhaar
                    // );

                    // console.log(
                    //     "urn:",
                    //     urn
                    // );


                    /*
                     * =========================================
                     * DIGILOCKER FAILED / CANCELLED
                     * =========================================
                     */

                    if (
                        eaadhaar?.toUpperCase() !== "Y"
                    ) {

                        throw new Error(
                            "We couldn’t complete your DigiLocker verification. Please try again to continue your application."
                        );

                    }


                    /*
                     * =========================================
                     * DIGILOCKER SUCCESS
                     * =========================================
                     */

                    const response =
                        await completeDigiLocker(
                            eaadhaar
                        );


                    // console.log(
                    //     "DigiLocker completion response:",
                    //     response
                    // );


                    /*
                     * Backend is source of truth.
                     *
                     * Store updated journey in Redux.
                     */

                    dispatch(
                        setJourney(
                            response.data.journey
                        )
                    );


                    /*
                     * =========================================
                     * RETURN TO DASHBOARD
                     * =========================================
                     *
                     * The callback URL is a direct router
                     * route, so LoanApplicationFlow is not
                     * the parent of this component.
                     *
                     * Therefore navigate directly.
                     */

                    navigate(
                        "/ndashboard",
                        {
                            replace: true,
                        }
                    );

                } catch (error) {

                    let message =
                        "Unable to verify DigiLocker.";

                    if (error instanceof ApiError) {

                        if (
                            error.status >= 500 &&
                            error.status < 600
                        ) {
                            message =
                                "Something went wrong. Please try again later.";
                        } else {
                            message =
                                error.message?.trim() ||
                                message;
                        }
                    }

                    setError(message);
                }

            };


        handleDigiLockerCallback();

    }, [
        dispatch,
        navigate,
    ]);

    const handleTryAgain = async () => {

        if (isRetrying) {
            return;
        }

        try {

            setIsRetrying(true);

            const response =
                await journeyAction({
                    subStep: "DIGILOCKER",
                    action: "GENERATE",
                    payload: {
                        redirectUrl:
                            "https://stg-native.cleverpe.com/ndashboard/kyc/digilocker/callback",
                    },
                });


            dispatch(
                setJourney(
                    response.data.journey
                )
            );


            const digiLockerLink =
                response.data.metadata?.link;


            if (!digiLockerLink) {

                throw new Error(
                    "Unable to generate DigiLocker link."
                );

            }


            // Only after successful generation,
            // redirect to DigiLocker.
            window.location.href =
                digiLockerLink;

        } catch (error) {

            let message =
                "Unable to start DigiLocker verification.";

            if (error instanceof ApiError) {

                if (
                    error.status >= 500 &&
                    error.status < 600
                ) {
                    message =
                        "Something went wrong. Please try again later.";
                } else {
                    message =
                        error.message?.trim() ||
                        message;
                }
            }

            setError(message);

        } finally {

            setIsRetrying(false);

        }
    };


    return (

        <MobileFrame
            glowColor="#c7d2fe"
            borderColor="#e5e7eb"
            width={420}
        >

            <div className="relative w-full h-full overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100">


                {/* Background blobs */}

                <div
                    className="
                        absolute
                        w-64
                        h-64
                        bg-purple-200
                        rounded-full
                        -top-24
                        -left-20
                        blur-3xl
                        opacity-20
                    "
                />

                <div
                    className="
                        absolute
                        w-64
                        h-64
                        bg-blue-200
                        rounded-full
                        -bottom-24
                        -right-20
                        blur-3xl
                        opacity-20
                    "
                />


                <div
                    className="
                        relative
                        z-10
                        flex
                        h-full
                        w-full
                        flex-col
                        px-6
                        pt-6
                        pb-4
                    "
                >
                    {/* Header */}

                    <div className="flex items-center justify-between">

                        <button
                            type="button"
                            onClick={() =>
                                navigate(
                                    "/ndashboard",
                                    {
                                        replace: true,
                                    }
                                )
                            }
                            aria-label="Go back to dashboard"
                            className="
            flex
            h-9
            w-9
            items-center
            justify-center
            rounded-full
            border
            border-slate-200
            bg-white
            text-gray-600
            shadow-sm
            transition-transform
            active:scale-95
        "
                        >
                            <ChevronLeft
                                className="h-4.5 w-4.5"
                                aria-hidden="true"
                            />
                        </button>

                        <img
                            src="/CleverPeLogos/CleverPE_Black_icon.svg"
                            alt="CleverPe"
                            className="h-6 w-6 opacity-90"
                        />

                    </div>

                    {/* Main Content */}

                    <div
                        className="
        flex
        flex-1
        flex-col
        items-center
        justify-center
    "
                    >
                        {error ? (

                            <div className="w-full text-center">

                                <div
                                    className="
                    mx-auto
                    flex
                    h-10
                    w-10
                    items-center
                    justify-center
                    rounded-full
                    bg-red-50
                "
                                >
                                    <span className="text-lg font-semibold text-red-500">
                                        !
                                    </span>
                                </div>

                                <h2
                                    className="
                    mt-3
                    text-base
                    font-semibold
                    text-gray-900
                "
                                >
                                    DigiLocker Verification Failed
                                </h2>

                                <p
                                    className="
                    mt-1.5
                    text-xs
                    leading-relaxed
                    text-gray-500
                "
                                >
                                    {error}
                                </p>

                            </div>

                        ) : (

                            <div
                                className="
                flex
                flex-col
                items-center
                justify-center
            "
                            >

                                <Lottie
                                    animationData={
                                        kycLoaderAnimation
                                    }
                                    loop
                                    autoplay
                                    className="h-40 w-40"
                                />

                                <p
                                    className="
                    mt-2
                    text-center
                    text-[13px]
                    font-medium
                    text-gray-500
                "
                                >
                                    Verifying your DigiLocker documents...
                                </p>

                            </div>

                        )}
                    </div>

                    {/* Footer Action */}

                    {error && (
                        <button
                            type="button"
                            onClick={handleTryAgain}
                            disabled={isRetrying}
                            className="
            mb-2
            flex
            w-full
            items-center
            justify-center
            gap-2
            rounded-full
            bg-gradient-to-r
            from-indigo-500
            to-blue-400
            py-3
            text-sm
            font-semibold
            text-white
            transition
            hover:opacity-95
            active:scale-[0.98]
            disabled:cursor-not-allowed
            disabled:opacity-60
        "
                        >
                            {isRetrying && (
                                <Loader2
                                    className="h-4 w-4 animate-spin"
                                    aria-hidden="true"
                                />
                            )}

                            {isRetrying
                                ? "Opening DigiLocker..."
                                : "Try Again"}
                        </button>
                    )}

                    {/* Footer */}

                    <p
                        className="
        mt-2
        text-center
        text-[11px]
        font-semibold
        tracking-wide
        text-gray-400
    "
                    >
                        CleverPe
                    </p>

                </div>

            </div>

        </MobileFrame>

    );
};


export default DigiLockerCallback;